/**
 * Envolvente de amplitud del audio del paciente (tanda 4, D-4).
 *
 * El avatar mueve la boca según el nivel del audio que suena. **No se
 * mide en vivo**: enchufar el `HTMLAudioElement` a un `AnalyserNode`
 * obliga a reencaminar el audio por Web Audio, y con el contexto
 * suspendido —política de autoplay, Safari, pestaña en segundo plano— el
 * audio dejaría de oírse. El audio manda (P-3): antes que animar, suena.
 *
 * En su lugar se decodifica el mismo binario aparte y se precalcula el
 * nivel cada pocos milisegundos. Si la decodificación falla o tarda, la
 * reproducción no se entera y la boca se queda quieta.
 *
 * Este módulo no sabe de avatares ni de blend shapes: entrega números.
 * La compuerta, el ataque y la caída viven del lado del render, que es
 * quien conoce el ritmo de los frames.
 */

/** Resolución de la envolvente. 20 ms ≈ 50 valores por segundo. */
export const INTERVALO_ENVOLVENTE_MS = 20;

export interface EnvolventeAudio {
  /** Milisegundos que representa cada valor de `niveles`. */
  intervaloMs: number;
  /**
   * Nivel por intervalo, `0..1`, normalizado al pico del propio clip.
   * Normalizar por clip y no en absoluto hace que la boca se mueva igual
   * con una frase susurrada que con una enérgica: el TTS varía su
   * volumen entre respuestas y la amplitud absoluta no dice nada.
   */
  niveles: Float32Array;
}

/**
 * RMS por ventana, normalizado al pico. Pura: mismas muestras, misma
 * envolvente.
 *
 * @param muestras un canal de audio, valores `-1..1`.
 */
export function calcularEnvolvente(
  muestras: Float32Array,
  sampleRate: number,
  intervaloMs: number = INTERVALO_ENVOLVENTE_MS,
): EnvolventeAudio {
  const porVentana = Math.max(1, Math.round((sampleRate * intervaloMs) / 1000));
  const total = Math.ceil(muestras.length / porVentana);
  const niveles = new Float32Array(total);

  let pico = 0;
  for (let v = 0; v < total; v++) {
    const desde = v * porVentana;
    const hasta = Math.min(desde + porVentana, muestras.length);
    let suma = 0;
    for (let i = desde; i < hasta; i++) {
      const m = muestras[i] ?? 0;
      suma += m * m;
    }
    const rms = Math.sqrt(suma / Math.max(1, hasta - desde));
    niveles[v] = rms;
    if (rms > pico) pico = rms;
  }

  // Un clip en silencio queda en ceros en vez de amplificar el ruido de
  // fondo hasta 1.
  if (pico > 1e-4) {
    for (let v = 0; v < total; v++) niveles[v] = (niveles[v] ?? 0) / pico;
  } else {
    niveles.fill(0);
  }

  return { intervaloMs, niveles };
}

/**
 * Nivel en un instante, interpolado entre los dos valores vecinos. Fuera
 * del clip devuelve 0, así que un desfase entre el reloj y el audio se
 * nota como boca quieta, nunca como boca trabada.
 */
export function nivelEn(envolvente: EnvolventeAudio, ms: number): number {
  const { intervaloMs, niveles } = envolvente;
  if (niveles.length === 0 || ms < 0) return 0;
  const pos = ms / intervaloMs;
  const i = Math.floor(pos);
  if (i >= niveles.length) return 0;
  const a = niveles[i] ?? 0;
  const b = niveles[i + 1] ?? 0;
  return a + (b - a) * (pos - i);
}

/** Duración total que cubre la envolvente. */
export function duracionMs(envolvente: EnvolventeAudio): number {
  return envolvente.niveles.length * envolvente.intervaloMs;
}

/** Lo mínimo de un `AudioBuffer` que hace falta acá. */
export interface BufferDecodificado {
  sampleRate: number;
  getChannelData(canal: number): Float32Array;
}

/**
 * Lo que `envolventeDeAudio` necesita de un `AudioContext`. Tipado
 * estructural y no `AudioBuffer`, para poder testear sin DOM.
 */
export interface DecodificadorAudio {
  decodeAudioData(datos: ArrayBuffer): Promise<BufferDecodificado>;
}

/**
 * Decodifica y calcula la envolvente. **Nunca lanza**: si el navegador no
 * decodifica el formato, devuelve `null` y el llamador sigue sin animar
 * la boca.
 *
 * Con varios canales usa el primero: para voz mono-ish de un TTS, mezclar
 * no cambia la envolvente y cuesta el doble.
 */
export async function envolventeDeAudio(
  binario: ArrayBuffer,
  decodificador: DecodificadorAudio,
  intervaloMs: number = INTERVALO_ENVOLVENTE_MS,
): Promise<EnvolventeAudio | null> {
  try {
    // `decodeAudioData` puede neutrar el buffer: se le pasa una copia,
    // porque el mismo binario lo está usando la reproducción.
    const buffer = await decodificador.decodeAudioData(binario.slice(0));
    return calcularEnvolvente(buffer.getChannelData(0), buffer.sampleRate, intervaloMs);
  } catch {
    return null;
  }
}
